import { FC, useState } from 'react';
import classNames from 'classnames';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useTranslations } from 'next-intl';
import { useFormatDate } from 'hooks/useFormatDate';
import { DateTimePicker } from 'elements/DateTimePicker';
import { roundUpToSlot } from 'elements/TimePicker';
import { useMemoFormContext } from '../context/FormContext';

interface Props {
  // The day a new memo is dated to unless the user picks one, e.g. the
  // Journal day that is open. Seeds the picker when none is chosen yet.
  defaultDatedAt?: Date | null;
}

// The composer's Date pill. Every memo has a date, so it's offered for
// every type. At rest it reads "+ Date"; with a date chosen it shows it.
// Clicking opens one picker with the day and the time, like the type
// picker, with Done and (once a date is set) Remove.
export const DateChip: FC<Props> = ({ defaultDatedAt }) => {
  const t = useTranslations();
  const formatDate = useFormatDate();
  const { watch, setValue } = useMemoFormContext();

  const raw = watch('datedAt') as Date | string | null | undefined;
  const value = raw ? new Date(raw) : null;

  const pick = (next: Date | null) =>
    setValue('datedAt', next, { shouldDirty: true });

  return (
    <Popover>
      {({ close }) => (
        <>
          <PopoverButton
            className={classNames(
              styles.chip,
              value ? styles.active : styles.inactive,
            )}
          >
            {value ? (
              <>
                <FontAwesomeIcon icon={faCalendar} className={styles.icon} />
                <span>
                  {formatDate(value, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className={styles.icon} />
                <span>{t('memoComposer.date')}</span>
              </>
            )}
          </PopoverButton>

          <PopoverPanel
            anchor={{ to: 'bottom start', gap: '6px' }}
            className={styles.panel}
          >
            <DatePanel
              initial={value ?? defaultDatedAt ?? roundUpToSlot(new Date())}
              showsRemove={value !== null}
              onDone={(next) => {
                pick(next);
                close();
              }}
              onRemove={() => {
                pick(null);
                close();
              }}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

// The open picker. Keeps its own selection until Done, so browsing days
// doesn't change the memo's date yet. Mounted fresh each time it opens.
const DatePanel: FC<{
  initial: Date;
  showsRemove: boolean;
  onDone: (next: Date) => void;
  onRemove: () => void;
}> = ({ initial, showsRemove, onDone, onRemove }) => {
  const t = useTranslations();
  const [selection, setSelection] = useState(initial);

  return (
    <>
      <DateTimePicker
        id="memo-date"
        value={selection}
        onChange={setSelection}
      />
      <div className={styles.footer}>
        {showsRemove && (
          <button type="button" onClick={onRemove} className={styles.remove}>
            {t('common.remove')}
          </button>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => onDone(selection)}
          className={styles.done}
        >
          {t('common.done')}
        </button>
      </div>
    </>
  );
};

const styles = {
  // Same shape as the other chips (ToggleBtn).
  chip: classNames(
    'inline-flex items-center gap-1 py-1 px-2.5 border rounded-full text-xs',
    'focus:outline-hidden',
  ),
  inactive:
    'bg-surface-card text-ink-secondary border-stroke hover:bg-surface-field',
  active:
    'bg-accent-tint text-accent-tint-ink font-medium border-accent-tint-ink/30',
  icon: 'text-[9px]',
  // Same surface as the type picker's panel.
  panel: classNames(
    'z-[60] w-[320px] rounded-xl bg-surface-overlay shadow-lg ring-1 ring-hairline',
    'focus:outline-hidden',
  ),
  footer: 'flex items-center gap-2 border-t border-hairline px-3 py-2.5',
  remove: classNames(
    'rounded-full bg-red-500/12 px-3 py-1.5 text-xs font-semibold text-red-600',
    'hover:bg-red-500/20 focus:outline-hidden',
  ),
  done: classNames(
    'rounded-full bg-accent-fill px-3.5 py-1.5 text-xs font-semibold text-white',
    'hover:brightness-110 focus:outline-hidden',
  ),
};
